import type { FC } from "react";
import { useState, useEffect, useMemo } from "react";
import {
  Button,
  ApiKeyCreateModal,
  IApiKeyCreateData,
  ApiKeyCredentialsModal,
  ApiKeyCredentials,
  DialogForm,
  TableSkeleton,
  StandardTable,
} from "@components";
import { useWorkspaceUsers } from "../../../hooks";
import { useUserSession } from "../../../context/UserContext";
import { useWorkspace } from "../../../context/WorkspaceContext";
import { useCache } from "../../../context/CacheContext";
import { ITableRow, Timestamp } from "../../../types";
import { useTranslation } from "react-i18next";
import { HiOutlinePlus, HiTrash, HiOutlineKey } from "react-icons/hi";
import { createApiKey as apiCreateApiKey } from "../../../api";
import { WorkspaceAccess } from "../../../models";
import PublicFormConfig from "../../../models/PublicFormConfig";
import { WorkspaceRole } from "../../../types";
import { SettingsRowProps } from "../components/SettingsRow";

interface ApiKeysSettingsContentProps {
  SettingsRow: React.ComponentType<SettingsRowProps>;
}

const isVisibleApiKey = (user: WorkspaceAccess): boolean => {
  return user.isApiKey === true && !user.name?.startsWith("MCP:");
};

const ApiKeysSettingsContent: FC<ApiKeysSettingsContentProps> = ({
  SettingsRow,
}) => {
  const [isLoading, setLoading] = useState(true);
  const [tableRows, setTableRows] = useState([] as ITableRow[]);
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [isCreatingApiKey, setCreatingApiKey] = useState(false);
  const [isCredentialsModalOpen, setCredentialsModalOpen] = useState(false);
  const [currentCredentials, setCurrentCredentials] =
    useState<ApiKeyCredentials | null>(null);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [apiKeyData, setApiKeyData] = useState(WorkspaceAccess.default());

  const { t } = useTranslation(["common", "user", "workspace"]);
  const { activeWorkspace } = useWorkspace();
  const { userSession } = useUserSession();
  const { updateCacheKey } = useCache();
  const query = useWorkspaceUsers();

  const isQueryLoading = useMemo(
    () => query.isLoading || query.isRefetching,
    [query],
  );

  const queryData = useMemo(() => query.data ?? [], [query]);

  // Keep track of the actual apiKeys array to check its length for the empty state
  const [apiKeys, setApiKeys] = useState<WorkspaceAccess[]>([]);

  useEffect(() => {
    setLoading(isQueryLoading);
  }, [isQueryLoading]);

  useEffect(() => {
    if (!isQueryLoading && queryData.length > 0) {
      // Filter to get only API key rows and sort by creation date (newest first)
      const apiKeyUsers = queryData
        .filter(isVisibleApiKey)
        .sort((a, b) => {
          // Sort by createdAt descending (newest first)
          if (!a.createdAt && !b.createdAt) return 0;
          if (!a.createdAt) return 1;
          if (!b.createdAt) return -1;
          return b.createdAt.seconds - a.createdAt.seconds;
        });
      setApiKeys(apiKeyUsers);

      const rows = apiKeyUsers.map((user) => ({
        id: user.name,
        cells: [
          {
            id: "name",
            children: user.name ?? user.email,
            showOnSmallScreen: true,
          },
          {
            id: "role",
            children: t(`workspace:roles.${user.role}`),
          },
          {
            id: "createdAt",
            children: Timestamp.toDisplayDate(user.createdAt),
          },
        ],
        actions: [
          {
            id: "delete",
            icon: <HiTrash />,
            color: "failure",
            onActionClick: () => handleDeleteClick(user),
          },
        ],
      }));
      setTableRows(rows);
    } else if (!isQueryLoading) {
      setApiKeys([]);
      setTableRows([]);
    }
  }, [queryData, isQueryLoading, t]);

  const handleDeleteClick = (user: WorkspaceAccess) => {
    setApiKeyData(user);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = () => {
    WorkspaceAccess.deleteWorkspaceUser(apiKeyData);
    setDeleteModalOpen(false);
    updateCacheKey();
    setLoading(true);
  };

  const handleCreateApiKey = async (data: IApiKeyCreateData) => {
    if (!userSession?.token?.accessToken || !activeWorkspace?.workspaceId) {
      return;
    }

    try {
      setCreatingApiKey(true);
      const credentials = await apiCreateApiKey(
        userSession.token.accessToken,
        activeWorkspace.workspaceId,
        data.name,
        data.role,
      );

      if (credentials && credentials.client_id) {
        if (data.role === WorkspaceRole.OrderDraft) {
          try {
            const existingConfig = await PublicFormConfig.getByWorkspaceId(
              activeWorkspace.workspaceId,
            );

            if (!existingConfig) {
              const modelDefaults = PublicFormConfig.default().data();
              const defaultConfig = {
                ...modelDefaults,
                allowCustomOrderFlow: false,
                allowProductListOrderFlow: false,
                workspaceId: activeWorkspace.workspaceId,
              };

              await PublicFormConfig.create(
                activeWorkspace.workspaceId,
                defaultConfig,
              );
            }
          } catch (configError) {
            console.warn(
              "Failed to create default order form config:",
              configError,
            );
          }
        }

        setCurrentCredentials(credentials);
        setCredentialsModalOpen(true);
        setCreateModalOpen(false);
      }
    } catch (error) {
      console.error(
        "ApiKeysSettingsContent: handleCreateApiKey - Failed to create API key:",
        error,
      );
    } finally {
      setCreatingApiKey(false);
    }
  };

  const handleCloseCredentialsModal = () => {
    setCredentialsModalOpen(false);
    setCurrentCredentials(null);
    query.refetch();
    updateCacheKey();
  };

  const tableHeader = [
    {
      id: "name",
      children: t("user:users.name"),
      showOnSmallScreen: true,
    },
    {
      id: "role",
      children: t("user:role"),
    },
    {
      id: "createdAt",
      children: t("common:created_at"),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start">
        <div>
          <h2 className="text-xl font-semibold text-text sm:text-2xl">
            {t("user:api_keys_title")}
          </h2>
          <p className="text-secondary text-base pt-2 pb-4 dark:text-secondary">
            {t("user:api_keys_description")}
          </p>
        </div>
        <Button
          color="light"
          onClick={() => setCreateModalOpen(true)}
          type="button"
          className="mt-2 sm:mt-0"
        >
          <HiOutlinePlus className="mr-2 h-5 w-5" />
          {t("workspace:api_keys.new_button")}
        </Button>
      </div>

      <div className="flex flex-col">
        {isLoading ? (
          <TableSkeleton />
        ) : apiKeys.length === 0 ? (
          <div className="text-center py-8">
            <HiOutlineKey className="mx-auto h-12 w-12 text-muted" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
              {t("workspace:api_keys.empty_state.title")}
            </h3>
            <p className="mt-1 text-sm text-muted">
              {t("workspace:api_keys.empty_state.description")}
            </p>
          </div>
        ) : (
          <StandardTable
            header={tableHeader}
            rows={tableRows}
            hasActions={true}
            compact={true}
          />
        )}
      </div>

      <ApiKeyCreateModal
        isShowing={isCreateModalOpen}
        onConfirm={handleCreateApiKey}
        onCancel={() => setCreateModalOpen(false)}
        isSubmitting={isCreatingApiKey}
      />

      <ApiKeyCredentialsModal
        isShowing={isCredentialsModalOpen}
        credentials={currentCredentials}
        onClose={handleCloseCredentialsModal}
      />

      <DialogForm
        title={t("user:delete_api_key_title") + " " + apiKeyData.name + "?"}
        show={isDeleteModalOpen}
        toggleModal={() => setDeleteModalOpen(false)}
        showHeader={false}
        buttonConfirmColor="failure"
        dismissible
        confirmButton={handleDeleteConfirm}
        buttonConfirmText={t("common:confirm")}
        buttonConfirmPrependIcon={<HiTrash className="mr-2 h-4 w-4" />}
        buttonCloseText={t("common:close")}
      />
    </div>
  );
};

export default ApiKeysSettingsContent;
